import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthTokenService } from './auth-token.service';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  basePath = environment.apiBaseUrl;

  logOutSource = new BehaviorSubject(false);

  constructor(
    private http: HttpClient,
    private logger: LoggerService,
    private authTokens: AuthTokenService,
  ) {
    this.handleError = this.handleError.bind(this);
  }

  handleError(error: any) {
    if (!(error instanceof HttpErrorResponse)) {
      console.error('Authentication request failed:', error?.message || error);
      if (error?.code === 'NotAuthorizedException') {
        this.logger.log('Cognito refresh token rejected, logging out.');
        this.logOutSource.next(true);
      }
      return throwError(() => error);
    }

    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      console.error(
        `Backend returned code ${error.status}, body was: ${error.error}`,
      );

      if (error.status === 401) {
        this.logger.log('Unauthorized request, logging out.');
        this.logOutSource.next(true);
      }
    }

    return throwError(() => error.error);
  }

  private authHeaders(headers: Record<string, string> = {}): Promise<HttpHeaders> {
    return this.authTokens.getValidAccessToken().then(
      (token) =>
        new HttpHeaders({
          ...headers,
          Authorization: `Bearer ${token || ''}`,
        }),
    );
  }

  private requestWithAuth<T>(
    headers: Record<string, string>,
    requestFactory: (headers: HttpHeaders) => Observable<T>,
  ): Observable<T> {
    return from(this.authHeaders(headers)).pipe(
      switchMap((authHeaders) => requestFactory(authHeaders)),
      catchError((error) => this.handleError(error)),
    );
  }

  postJsonAsForm(url: string, item: object): Observable<any> {
    const form = new FormData();
    form.append('json', JSON.stringify(item));
    return this.post(url, form, true);
  }

  postWithPayloadHeader(url: string, payload: object): Observable<any> {
    const jsonStr = JSON.stringify(payload);
    const base64Payload = btoa(unescape(encodeURIComponent(jsonStr)));
    return this.requestWithAuth(
      {
        'Content-Type': 'application/json',
        'X-Smartlink-Payload': base64Payload,
      },
      (headers) =>
        this.http.post<any>(`${this.basePath}/${url}`, '{}', { headers }),
    );
  }

  post(url: string, item: any, isFormData = false): Observable<any> {
    if (isFormData) {
      return this.requestWithAuth({}, (headers) =>
        this.http.post<any>(`${this.basePath}/${url}`, item, { headers }),
      );
    }

    return this.requestWithAuth({ 'Content-Type': 'application/json' }, (headers) =>
      this.http.post<any>(`${this.basePath}/${url}`, JSON.stringify(item), {
        headers,
      }),
    );
  }

  postFile(url: string, item: any, force = false): Observable<any> {
    return this.requestWithAuth({}, (headers) =>
      this.http.post<any>(`${this.basePath}/${url}`, item, { headers }),
    );
  }

  postBlob(url: string, item: any): Observable<HttpResponse<Blob>> {
    return this.requestWithAuth({ 'Content-Type': 'application/json' }, (headers) =>
      this.http.post<Blob>(`${this.basePath}/${url}`, JSON.stringify(item), {
        headers,
        responseType: 'blob' as 'json',
        observe: 'response',
      }),
    );
  }

  get(url: string, params?: HttpParams): Observable<any> {
    return this.requestWithAuth({ 'Content-Type': 'application/json' }, (headers) =>
      this.http.get<any>(`${this.basePath}/${url}`, {
        headers,
        params,
      }),
    );
  }

  getBlob(url: string, params?: HttpParams): Observable<HttpResponse<Blob>> {
    return this.requestWithAuth({}, (headers) =>
      this.http.get(`${this.basePath}/${url}`, {
        headers,
        params,
        responseType: 'blob' as const,
        observe: 'response' as const,
      }),
    );
  }

  put(url: string, item: any): Observable<any> {
    return this.requestWithAuth({ 'Content-Type': 'application/json' }, (headers) =>
      this.http.put<any>(`${this.basePath}/${url}`, JSON.stringify(item), {
        headers,
      }),
    );
  }

  patch(url: string, item: any): Observable<any> {
    return this.requestWithAuth({ 'Content-Type': 'application/json' }, (headers) =>
      this.http.patch<any>(`${this.basePath}/${url}`, JSON.stringify(item), {
        headers,
      }),
    );
  }

  delete(url: string, params?: HttpParams): Observable<any> {
    return this.requestWithAuth({ 'Content-Type': 'application/json' }, (headers) =>
      this.http.delete<any>(`${this.basePath}/${url}`, { headers, params }),
    );
  }

  getNoAuth(url: string, params?: HttpParams): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      params: params ?? undefined,
    };
    return this.http
      .get<any>(`${this.basePath}/${url}`, httpOptions)
      .pipe(catchError((error) => this.handleError(error)));
  }

  postNoAuth(url: string, item: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http
      .post<any>(`${this.basePath}/${url}`, JSON.stringify(item), httpOptions)
      .pipe(catchError((error) => this.handleError(error)));
  }
}
